// W36 (2026-10-20): the Build-an-order screen's hooks for the pricing
// engine and the secure-payment-link gate on a REAL session.
//
//   useEnginePricing  two POST /v1/pricing/calculate quotes through
//                     data/livePricing.ts: the catalog quote (every known
//                     product at 1 unit, no bundles) that prices the
//                     product tiles, and the basket quote that prices the
//                     order summary (tier, 20% subscription line, bundle
//                     line, products subtotal). Tax and total stay
//                     "pending" by contract.
//   useSecureLinkGate GET /v1/gates/LIVE_WRITE_GATE_PAYMENT_PREPARE_SECURE_LINK
//                     only while the payment dialog is open; anything but
//                     a 200 is "unknown" and keeps the button inert.
//   prepareSecureLink POST /v1/actions/PAYMENT_PREPARE_SECURE_LINK with an
//                     X-Idempotency-Key; only called when the gate is ON.
//
// Every request is a RELATIVE fetch with buildSessionHeaders(session),
// the same contract useContactProfile / useContactAddresses use. Preview
// and dev-mock sessions never fetch (useContactReadGate says no).

import { useEffect, useMemo, useRef, useState } from 'react';
import { buildSessionHeaders, useContactReadGate } from '../_shared/useContactProfile.js';
import {
  createEnginePricingClient,
  type EngineLine,
  type EngineQuote,
  type PurchaseType,
} from '../../data/livePricing.js';

export type EngineStatus = 'skipped' | 'loading' | 'ready' | 'pending' | 'error';

export interface EngineCatalogState {
  status: EngineStatus;
  /** Catalog-quote lines keyed by engine handle. */
  prices: Record<string, EngineLine>;
  quote: EngineQuote | null;
  errorCode: string | null;
}

export interface EngineBasketState {
  status: EngineStatus;
  quote: EngineQuote | null;
  errorCode: string | null;
  /** The contextKey this quote answers; a quote for a stale key is dropped. */
  contextKey: string | null;
}

export interface EnginePricingArgs {
  customerId: string;
  workId: string;
  /** false in preview: nothing is fetched and both states are 'skipped'. */
  enabled: boolean;
  purchaseType: PurchaseType;
  cadenceDays: string;
  basket: Array<{ handle: string; units: number }>;
  /** Changes whenever basket, purchase type or cadence change. */
  contextKey: string;
}

export interface EnginePricing {
  catalog: EngineCatalogState;
  basket: EngineBasketState;
  /** Session headers for sibling calls (gate read, secure-link action); null off-session. */
  headers: () => Record<string, string> | null;
}

export const SECURE_LINK_GATE = 'LIVE_WRITE_GATE_PAYMENT_PREPARE_SECURE_LINK';
export const SECURE_LINK_ACTION = 'PAYMENT_PREPARE_SECURE_LINK';
export const DRAFT_ORDER_GATE = 'LIVE_WRITE_GATE_SHOPIFY_CREATE_DRAFT_ORDER';

export function purchaseTypeFor(subscription: boolean): PurchaseType {
  return subscription ? 'subscription' : 'one_time';
}

const SKIPPED_CATALOG: EngineCatalogState = { status: 'skipped', prices: {}, quote: null, errorCode: null };
const SKIPPED_BASKET: EngineBasketState = { status: 'skipped', quote: null, errorCode: null, contextKey: null };

export function useEnginePricing(args: EnginePricingArgs): EnginePricing {
  const { customerId, workId, enabled, purchaseType, cadenceDays, basket, contextKey } = args;
  const gate = useContactReadGate(enabled ? customerId : null);
  const canFetch = enabled && gate.canFetch && !!gate.session;
  const { sessionIdentity, sessionToken } = gate;
  const session = gate.session;

  const headers = useMemo<() => Record<string, string> | null>(
    () => () => (session ? buildSessionHeaders(session) : null),
    // Re-mint on token refresh / identity change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionIdentity, sessionToken],
  );
  const client = useMemo(() => createEnginePricingClient({ headers }), [headers]);

  const [catalog, setCatalog] = useState<EngineCatalogState>(() => (canFetch ? { ...SKIPPED_CATALOG, status: 'loading' } : SKIPPED_CATALOG));
  const [basketState, setBasketState] = useState<EngineBasketState>(SKIPPED_BASKET);
  const latestCatalog = useRef(0);
  const latestBasket = useRef(0);

  // Catalog quote: once per session + purchase type.
  useEffect(() => {
    if (!canFetch) {
      setCatalog(SKIPPED_CATALOG);
      return;
    }
    const requestId = ++latestCatalog.current;
    setCatalog((current) => ({ ...current, status: 'loading', errorCode: null }));
    client.quote({ hubspotContactId: customerId, workId, purchaseType, cadenceDays, purpose: 'catalog', lineItems: [] })
      .then((result) => {
        if (latestCatalog.current !== requestId) return;
        if (result.kind === 'ok') {
          const prices: Record<string, EngineLine> = {};
          for (const line of result.quote.lineItems) prices[line.productHandle] = line;
          setCatalog({ status: 'ready', prices, quote: result.quote, errorCode: null });
        } else {
          setCatalog({ status: result.kind === 'pending' ? 'pending' : 'error', prices: {}, quote: null, errorCode: result.errorCode });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, customerId, workId, purchaseType, cadenceDays, sessionIdentity, sessionToken]);

  // Basket quote: on every material change while the basket is non-empty.
  const basketKey = JSON.stringify(basket);
  useEffect(() => {
    const requestId = ++latestBasket.current;
    if (!canFetch || basket.length === 0) {
      setBasketState(SKIPPED_BASKET);
      return;
    }
    setBasketState({ status: 'loading', quote: null, errorCode: null, contextKey });
    client.quote({
      hubspotContactId: customerId,
      workId,
      purchaseType,
      cadenceDays,
      purpose: 'basket',
      lineItems: basket.map((line) => ({ productHandle: line.handle, units: line.units, hasActiveSub: purchaseType === 'subscription' })),
    }).then((result) => {
      if (latestBasket.current !== requestId) return;
      if (result.kind === 'ok') {
        setBasketState({ status: 'ready', quote: result.quote, errorCode: null, contextKey });
      } else {
        setBasketState({ status: result.kind === 'pending' ? 'pending' : 'error', quote: null, errorCode: result.errorCode, contextKey });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, customerId, workId, purchaseType, cadenceDays, basketKey, contextKey, sessionIdentity, sessionToken]);

  return { catalog, basket: basketState, headers };
}

// --- Secure payment link gate + action --------------------------------

export type GateState = 'unknown' | 'loading' | 'on' | 'off';

export function useSecureLinkGate({ enabled, headers, fetchImpl }: {
  enabled: boolean;
  headers: () => Record<string, string> | null;
  fetchImpl?: typeof fetch;
}): GateState {
  const [state, setState] = useState<GateState>('unknown');
  const latest = useRef(0);
  useEffect(() => {
    const requestId = ++latest.current;
    const sessionHeaders = enabled ? headers() : null;
    if (!sessionHeaders) {
      setState('unknown');
      return;
    }
    setState('loading');
    const doFetch: typeof fetch = fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
    doFetch(`/v1/gates/${SECURE_LINK_GATE}`, { method: 'GET', headers: sessionHeaders })
      .then(async (response) => {
        if (latest.current !== requestId) return;
        if (!response.ok) {
          setState('unknown');
          return;
        }
        let body: unknown = null;
        try {
          body = await response.json();
        } catch {
          body = null;
        }
        const on = !!body && typeof body === 'object' && (body as { on?: unknown }).on === true;
        setState(on ? 'on' : 'off');
      })
      .catch(() => {
        if (latest.current === requestId) setState('unknown');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, headers]);
  return state;
}

export interface SecureLinkOutcome {
  ok: boolean;
  result: string;
  detail: string | null;
}

function idempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return `wk-${crypto.randomUUID()}`;
  return `wk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

/**
 * Fire PAYMENT_PREPARE_SECURE_LINK once (at-most-once key). The server
 * adapter answers with its own envelope; a draft order id is required
 * for the new_order context, so until the draft-order gate is on the
 * honest outcome is the server's validation code, surfaced verbatim.
 */
export async function prepareSecureLink({ headers, customerId, workId, fetchImpl }: {
  headers: () => Record<string, string> | null;
  customerId: string;
  workId: string;
  fetchImpl?: typeof fetch;
}): Promise<SecureLinkOutcome> {
  const sessionHeaders = headers();
  if (!sessionHeaders) return { ok: false, result: 'SESSION_UNAVAILABLE', detail: null };
  const doFetch: typeof fetch = fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  let response: Response;
  try {
    response = await doFetch(`/v1/actions/${SECURE_LINK_ACTION}`, {
      method: 'POST',
      headers: { ...sessionHeaders, 'Content-Type': 'application/json', 'X-Idempotency-Key': idempotencyKey() },
      body: JSON.stringify({ payload: { context: 'new_order', customerId, workId }, customerId }),
    });
  } catch (err) {
    return { ok: false, result: 'NETWORK_ERROR', detail: err instanceof Error ? err.message : null };
  }
  let body: Record<string, unknown> | null = null;
  try {
    const parsed: unknown = await response.json();
    body = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    body = null;
  }
  const result = typeof body?.result === 'string' ? (body.result as string) : `HTTP_${response.status}`;
  const detail = typeof body?.errorCode === 'string' ? (body.errorCode as string)
    : typeof body?.gateReason === 'string' ? (body.gateReason as string)
      : null;
  return { ok: result === 'SUCCESS', result, detail };
}
